// CitedSummary — render an ATLAS call summary / KB-note body as real markdown
// with its `[^N]` citations turned into interactive `[N]` markers.
//
// The body is agent markdown: headings, lists, bold, and inline `[^N]` markers
// backed by a `[^N]: <transcript-line-id>` definition block. This component
// fetches the call's transcript lines, hands both to `prepareSummary` (which
// strips the definition block and rewrites each marker into a `#citation-N`
// link carrier), and swaps that carrier for a `[N]` marker: hover or focus it
// for the verbatim quote (speaker + timestamp + text), click it to land on the
// cited transcript line.
//
// Where the transcript is on the page (the expanded row on Desk > Calls &
// Meetings) a marker scrolls the transcript panel to the cited line and
// highlights it — the page publishes a `TranscriptAnchor` for that. Everywhere
// else (Client Knowledge, Comply > Call Intelligence, the KB-notes panel, the
// review drawer) there is no transcript to scroll, so the marker deep-links to
// the call on Calls & Meetings, carrying the line id so the reader still lands
// on the right line. There is deliberately no separate "Citations" list: the
// markers are the citations, and the transcript below is not duplicated.
//
// react-markdown does not render raw HTML by default (and no `rehype-raw` /
// `dangerouslySetInnerHTML` here), so the agent-authored body cannot inject
// markup; the quotes render as React children, never as markdown.

import { useMemo } from 'react';
import { Link } from 'react-router';
import type { Components } from 'react-markdown';
import { useCallCitationSources } from '@/lib/call-citation-sources';
import { cn } from '@/lib/utils';
import { HoverPopover } from '@/components/ui';
import {
    citationNumberFromHref,
    prepareSummary,
    type PreparedCitation,
} from '@/lib/meeting-note-citations';
import { useTranscriptAnchor } from '@/lib/transcript-anchor';
import { useSession } from '@/contexts/SessionContext';
import Markdown from './Markdown';
import { canOpenRoute } from '@/lib/frontends';

interface CitedSummaryProps {
    /** The markdown summary / KB-note body. */
    body: string;
    /** The call the summary belongs to — its transcript resolves the citations. */
    callId: string | null | undefined;
    className?: string;
}

const BASE_COMPONENTS: Components = {
    h1: (props) => (
        <h2
            className="text-card-title text-[rgb(var(--color-text-primary))] mt-4 mb-1.5 first:mt-0"
            {...props}
        />
    ),
    h2: (props) => (
        <h2
            className="text-card-title text-[rgb(var(--color-text-primary))] mt-4 mb-1.5 first:mt-0"
            {...props}
        />
    ),
    h3: (props) => (
        <h3
            className="text-body-medium font-semibold text-[rgb(var(--color-text-primary))] mt-3 mb-1"
            {...props}
        />
    ),
    p: (props) => <p className="my-1.5" {...props} />,
    ul: (props) => <ul className="list-disc pl-5 my-1.5 space-y-0.5" {...props} />,
    ol: (props) => <ol className="list-decimal pl-5 my-1.5 space-y-0.5" {...props} />,
    li: (props) => <li className="leading-relaxed" {...props} />,
    strong: (props) => (
        <strong className="font-semibold text-[rgb(var(--color-text-primary))]" {...props} />
    ),
    hr: () => <hr className="my-3 border-[rgb(var(--color-divider))]" />,
    code: (props) => (
        <code
            className="font-mono tabular-nums text-[0.85em] px-1 py-0.5 rounded bg-[rgb(var(--color-text-primary)/0.05)] text-[rgb(var(--color-text-secondary))]"
            {...props}
        />
    ),
};

const MARKER_CLASS =
    'align-baseline text-[0.9em] text-[rgb(var(--color-accent))] hover:underline ' +
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[rgb(var(--color-accent))] rounded-sm';

// The quotes behind one marker, plus what clicking it will do.
function CitationPopoverContent({
    citation,
    canFocusTranscript,
}: {
    citation: PreparedCitation;
    canFocusTranscript: boolean;
}) {
    return (
        <span className="block space-y-2">
            {citation.sources.map((source) => (
                <span key={source.id} className="block">
                    <span className="block">
                        <span className="text-annotation font-medium text-[rgb(var(--color-text-primary))]">
                            {source.speaker}
                        </span>{' '}
                        <span className="text-annotation font-mono tabular-nums text-[rgb(var(--color-text-tertiary))]">
                            {source.timestamp}
                        </span>
                    </span>
                    <span className="block text-annotation text-[rgb(var(--color-text-secondary))]">
                        &ldquo;{source.text}&rdquo;
                    </span>
                </span>
            ))}
            <span className="block text-annotation text-[rgb(var(--color-text-tertiary))]">
                {canFocusTranscript
                    ? 'Click to jump to this line in the transcript.'
                    : 'Click to open this call and its transcript.'}
            </span>
        </span>
    );
}

// One `[N]` marker. A button when the page renders the cited transcript, a deep
// link to Calls & Meetings otherwise, and inert prose when there is no call to
// point at. The quote is announced to screen readers through the trigger's own
// accessible name — the hover panel is `visibility: hidden` until shown, so it
// is not reachable by assistive tech on its own.
function CitationMarker({
    citation,
    callId,
}: {
    citation: PreparedCitation;
    callId: string | null | undefined;
}) {
    const anchor = useTranscriptAnchor();
    const { user } = useSession();
    // The fallback link goes to a Desk page. Show plain text when the user cannot open it.
    const canOpenCalls = canOpenRoute('/desk/calls-meetings', user?.personas ?? []);
    const focusableLineId = citation.lineIds.find((id) => anchor?.hasLine(id) ?? false);
    const canFocusTranscript = Boolean(anchor && focusableLineId);

    const spokenQuote = citation.sources
        .map((s) => `${s.speaker} at ${s.timestamp}: “${s.text}”`)
        .join(' ');
    const spokenAction = canFocusTranscript
        ? 'Jump to this line in the transcript.'
        : 'Open this call and its transcript.';
    const label = `Citation ${citation.number}.${spokenQuote ? ` ${spokenQuote}` : ''} ${spokenAction}`;

    const visible = <span aria-hidden="true">[{citation.number}]</span>;

    let trigger = <span className="align-baseline text-[0.9em]">{visible}</span>;
    if (canFocusTranscript && anchor) {
        trigger = (
            <button
                type="button"
                className={MARKER_CLASS}
                aria-label={label}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    anchor.focusLines(citation.lineIds);
                }}
            >
                {visible}
            </button>
        );
    } else if (callId && canOpenCalls) {
        const line = citation.lineIds[0];
        const query = new URLSearchParams({ call: callId });
        if (line) query.set('line', line);
        trigger = (
            <Link
                to={`/desk/calls-meetings?${query.toString()}`}
                className={MARKER_CLASS}
                aria-label={label}
                onClick={(e) => e.stopPropagation()}
            >
                {visible}
            </Link>
        );
    }

    // Nothing to reveal (transcript not loaded / line purged) — keep the marker
    // rather than inventing a popover with no quote in it.
    if (citation.sources.length === 0) return trigger;

    return (
        <HoverPopover
            inline
            overlay
            content={
                <CitationPopoverContent
                    citation={citation}
                    canFocusTranscript={canFocusTranscript}
                />
            }
            panelClassName="w-80"
        >
            {trigger}
        </HoverPopover>
    );
}

export default function CitedSummary({ body, callId, className }: CitedSummaryProps) {
    const { data } = useCallCitationSources(callId ?? null);

    const prepared = useMemo(() => prepareSummary(body, data ?? []), [body, data]);

    const components = useMemo<Components>(() => {
        const byNumber = new Map(prepared.citations.map((c) => [c.number, c]));
        return {
            ...BASE_COMPONENTS,
            a: (props) => {
                const href = typeof props.href === 'string' ? props.href : '';
                const number = citationNumberFromHref(href);
                const citation = number === null ? undefined : byNumber.get(number);
                if (citation) return <CitationMarker citation={citation} callId={callId} />;
                return (
                    <a
                        className="text-[rgb(var(--color-accent))] no-underline hover:underline"
                        {...props}
                    />
                );
            },
        };
    }, [prepared.citations, callId]);

    return (
        <div
            className={cn(
                'text-body-medium text-[rgb(var(--color-text-secondary))] leading-relaxed',
                className,
            )}
        >
            <Markdown components={components}>{prepared.markdown}</Markdown>
            {prepared.orphanedMarkers.length > 0 && (
                <p className="mt-3 text-annotation text-[rgb(var(--color-text-tertiary))] italic">
                    {prepared.orphanedMarkers.length} citation
                    {prepared.orphanedMarkers.length === 1 ? '' : 's'} could not be linked to a
                    transcript line — this summary predates citation grounding.
                </p>
            )}
        </div>
    );
}
